/**
 * @ngdoc service
 * @name validationService
 * @module flowingly.Shared.Angular
 *
 * @description A service for validating email pattern
 */

import IModelerNode from '@Shared.Angular/@types/modelerNode';
import { Services } from '@Shared.Angular/@types/services';
import { IModelerNodeLink } from '@Shared.Angular/@types/workflow';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';

declare const _: _.LoDashStatic;

angular
  .module('flowingly.services')
  .factory('validationService', validationService);

validationService.$inject = [
  '$sanitize',
  'flowinglyGatewayService',
  'flowinglyActivityService',
  'flowinglyModelUtilityService',
  'APP_CONFIG',
  '$sce'
];

function validationService(
  $sanitize,
  flowinglyGatewayService,
  flowinglyActivityService,
  flowinglyModelUtilityService,
  appConfig: Services.APP_CONFIG,
  $sce
) {
  let allowBacklinkFromExclusiveGateway = false;

  const service = {
    getEmailValidationPattern: getEmailValidationPattern,
    getNumberValidationPattern: getNumberValidationPattern,
    getPhoneNumberValidationPattern: getPhoneNumberValidationPattern,
    isLinkDisallowedForGateway: isLinkDisallowedForGateway,
    isLinkAllowedForNode: isLinkAllowedForNode,
    isLinkDisallowedForStartOrEndNode: isLinkDisallowedForStartOrEndNode,
    isValidBackLink: isValidBackLink,
    isXssVulnerableString: isXssVulnerableString,
    sanitizeString: sanitizeString,
    sanitizeHtml: sanitizeHtml,
    getAllowBacklinkFromExclusiveGateway: getAllowBacklinkFromExclusiveGateway,
    setAllowBacklinkFromExclusiveGateway: setAllowBacklinkFromExclusiveGateway,

    // basic validation
    isEmpty,
    hasSpace
  };

  return service;

  //////////// Public API Methods

  /**
   * Checks if a value is empty or not.
   *
   * [empty]
   *      null, undefined, "", " ",[],{}
   *
   * [the following are not empty even if they are falsey]
   *      false, 0
   *
   * Strings are trimmed.
   *
   * @param {any} val Value to check
   * @returns {boolean} true if empty else false
   */

  function getAllowBacklinkFromExclusiveGateway() {
    return allowBacklinkFromExclusiveGateway;
  }

  function setAllowBacklinkFromExclusiveGateway(value: boolean) {
    allowBacklinkFromExclusiveGateway = value;
  }

  function isEmpty(val) {
    if (_.isNil(val))
      // is null or undefined
      return true;
    else if (_.isArray(val)) return val.length == 0;
    else if (_.isObject(val)) return _.keys(val).length == 0;
    else if (_.isString(val)) return _.trim(val).length == 0;
    else return false;
  }

  function hasSpace(val) {
    return /\s/.test(val);
  }

  function isXssVulnerableString(stringValue) {
    // create the regex obj.
    //let regex = new RegExp(/<[^\w<>]*(?:[^<>"'\s]*:)?[^\w<>]*(?:\W*s\W*c\W*r\W*i\W*p\W*t|\W*f\W*o\W*r\W*m|\W*s\W*t\W*y\W*l\W*e|\W*s\W*v\W*g|\W*m\W*a\W*r\W*q\W*u\W*e\W*e|(?:\W*l\W*i\W*n\W*k|\W*o\W*b\W*j\W*e\W*c\W*t|\W*e\W*m\W*b\W*e\W*d|\W*a\W*p\W*p\W*l\W*e\W*t|\W*p\W*a\W*r\W*a\W*m|\W*i?\W*f\W*r\W*a\W*m\W*e|\W*b\W*a\W*s\W*e|\W*b\W*o\W*d\W*y|\W*m\W*e\W*t\W*a|\W*i\W*m\W*a?\W*g\W*e?|\W*v\W*i\W*d\W*e\W*o|\W*a\W*u\W*d\W*i\W*o|\W*b\W*i\W*n\W*d\W*i\W*n\W*g\W*s|\W*s\W*e\W*t|\W*i\W*s\W*i\W*n\W*d\W*e\W*x|\W*a\W*n\W*i\W*m\W*a\W*t\W*e)[^>\w])|(?:<\w[\s\S]*[\s\0\/]|['"])(?:formaction|style|background|src|lowsrc|ping|on(?:d(?:e(?:vice(?:(?:orienta|mo)tion|proximity|found|light)|livery(?:success|error)|activate)|r(?:ag(?:e(?:n(?:ter|d)|xit)|(?:gestur|leav)e|start|drop|over)?|op)|i(?:s(?:c(?:hargingtimechange|onnect(?:ing|ed))|abled)|aling)|ata(?:setc(?:omplete|hanged)|(?:availabl|chang)e|error)|urationchange|ownloading|blclick)|Moz(?:M(?:agnifyGesture(?:Update|Start)?|ouse(?:PixelScroll|Hittest))|S(?:wipeGesture(?:Update|Start|End)?|crolledAreaChanged)|(?:(?:Press)?TapGestur|BeforeResiz)e|EdgeUI(?:C(?:omplet|ancel)|Start)ed|RotateGesture(?:Update|Start)?|A(?:udioAvailable|fterPaint))|c(?:o(?:m(?:p(?:osition(?:update|start|end)|lete)|mand(?:update)?)|n(?:t(?:rolselect|extmenu)|nect(?:ing|ed))|py)|a(?:(?:llschang|ch)ed|nplay(?:through)?|rdstatechange)|h(?:(?:arging(?:time)?ch)?ange|ecking)|(?:fstate|ell)change|u(?:echange|t)|l(?:ick|ose))|m(?:o(?:z(?:pointerlock(?:change|error)|(?:orientation|time)change|fullscreen(?:change|error)|network(?:down|up)load)|use(?:(?:lea|mo)ve|o(?:ver|ut)|enter|wheel|down|up)|ve(?:start|end)?)|essage|ark)|s(?:t(?:a(?:t(?:uschanged|echange)|lled|rt)|k(?:sessione|comma)nd|op)|e(?:ek(?:complete|ing|ed)|(?:lec(?:tstar)?)?t|n(?:ding|t))|u(?:ccess|spend|bmit)|peech(?:start|end)|ound(?:start|end)|croll|how)|b(?:e(?:for(?:e(?:(?:scriptexecu|activa)te|u(?:nload|pdate)|p(?:aste|rint)|c(?:opy|ut)|editfocus)|deactivate)|gin(?:Event)?)|oun(?:dary|ce)|l(?:ocked|ur)|roadcast|usy)|a(?:n(?:imation(?:iteration|start|end)|tennastatechange)|fter(?:(?:scriptexecu|upda)te|print)|udio(?:process|start|end)|d(?:apteradded|dtrack)|ctivate|lerting|bort)|DOM(?:Node(?:Inserted(?:IntoDocument)?|Removed(?:FromDocument)?)|(?:CharacterData|Subtree)Modified|A(?:ttrModified|ctivate)|Focus(?:Out|In)|MouseScroll)|r(?:e(?:s(?:u(?:m(?:ing|e)|lt)|ize|et)|adystatechange|pea(?:tEven)?t|movetrack|trieving|ceived)|ow(?:s(?:inserted|delete)|e(?:nter|xit))|atechange)|p(?:op(?:up(?:hid(?:den|ing)|show(?:ing|n))|state)|a(?:ge(?:hide|show)|(?:st|us)e|int)|ro(?:pertychange|gress)|lay(?:ing)?)|t(?:ouch(?:(?:lea|mo)ve|en(?:ter|d)|cancel|start)|ime(?:update|out)|ransitionend|ext)|u(?:s(?:erproximity|sdreceived)|p(?:gradeneeded|dateready)|n(?:derflow|load))|f(?:o(?:rm(?:change|input)|cus(?:out|in)?)|i(?:lterchange|nish)|ailed)|l(?:o(?:ad(?:e(?:d(?:meta)?data|nd)|start)?|secapture)|evelchange|y)|g(?:amepad(?:(?:dis)?connected|button(?:down|up)|axismove)|et)|e(?:n(?:d(?:Event|ed)?|abled|ter)|rror(?:update)?|mptied|xit)|i(?:cc(?:cardlockerror|infochange)|n(?:coming|valid|put))|o(?:(?:(?:ff|n)lin|bsolet)e|verflow(?:changed)?|pen)|SVG(?:(?:Unl|L)oad|Resize|Scroll|Abort|Error|Zoom)|h(?:e(?:adphoneschange|l[dp])|ashchange|olding)|v(?:o(?:lum|ic)e|ersion)change|w(?:a(?:it|rn)ing|heel)|key(?:press|down|up)|(?:AppComman|Loa)d|no(?:update|match)|Request|zoom))[\s\0]*=/i);
    const regex = new RegExp(
      /(param=)|(Set.constructor)|(<\/EMBED>)|(redirect)|(¼script)|(\\"";)|(header\()|(javascript)( )*(:)+|(<a onmouseover=)|(<a .* onmouseover=)|(?:\W*s\W*c\W*r\W*i\W*p\W*t|\W*f\W*o\W*r\W*m|\W*s\W*t\W*y\W*l\W*e|\W*s\W*v\W*g|\W*m\W*a\W*r\W*q\W*u\W*e\W*e|(?:\W*l\W*i\W*n\W*k|\W*o\W*b\W*j\W*e\W*c\W*t|\W*e\W*m\W*b\W*e\W*d|\W*a\W*p\W*p\W*l\W*e\W*t|\W*p\W*a\W*r\W*a\W*m|\W*i?\W*f\W*r\W*a\W*m\W*e|\W*b\W*a\W*s\W*e|\W*b\W*o\W*d\W*y|\W*m\W*e\W*t\W*a|\W*i\W*m\W*a?\W*g\W*e?|\W*v\W*i\W*d\W*e\W*o|\W*a\W*u\W*d\W*i\W*o|\W*b\W*i\W*n\W*d\W*i\W*n\W*g\W*s|\W*s\W*e\W*t|\W*i\W*s\W*i\W*n\W*d\W*e\W*x|\W*a\W*n\W*i\W*m\W*a\W*t\W*e))(?:formaction|style|background|src|lowsrc|ping|on(?:d(?:e(?:vice(?:(?:orienta|mo)tion|proximity|found|light)|livery(?:success|error)|activate)|r(?:ag(?:e(?:n(?:ter|d)|xit)|(?:gestur|leav)e|start|drop|over)?|op)|i(?:s(?:c(?:hargingtimechange|onnect(?:ing|ed))|abled)|aling)|ata(?:setc(?:omplete|hanged)|(?:availabl|chang)e|error)|urationchange|ownloading|blclick)|Moz(?:M(?:agnifyGesture(?:Update|Start)?|ouse(?:PixelScroll|Hittest))|S(?:wipeGesture(?:Update|Start|End)?|crolledAreaChanged)|(?:(?:Press)?TapGestur|BeforeResiz)e|EdgeUI(?:C(?:omplet|ancel)|Start)ed|RotateGesture(?:Update|Start)?|A(?:udioAvailable|fterPaint))|c(?:o(?:m(?:p(?:osition(?:update|start|end)|lete)|mand(?:update)?)|n(?:t(?:rolselect|extmenu)|nect(?:ing|ed))|py)|a(?:(?:llschang|ch)ed|nplay(?:through)?|rdstatechange)|h(?:(?:arging(?:time)?ch)?ange|ecking)|(?:fstate|ell)change|u(?:echange|t)|l(?:ick|ose))|m(?:o(?:z(?:pointerlock(?:change|error)|(?:orientation|time)change|fullscreen(?:change|error)|network(?:down|up)load)|use(?:(?:lea|mo)ve|o(?:ver|ut)|enter|wheel|down|up)|ve(?:start|end)?)|essage|ark)|s(?:t(?:a(?:t(?:uschanged|echange)|lled|rt)|k(?:sessione|comma)nd|op)|e(?:ek(?:complete|ing|ed)|(?:lec(?:tstar)?)?t|n(?:ding|t))|u(?:ccess|spend|bmit)|peech(?:start|end)|ound(?:start|end)|croll|how)|b(?:e(?:for(?:e(?:(?:scriptexecu|activa)te|u(?:nload|pdate)|p(?:aste|rint)|c(?:opy|ut)|editfocus)|deactivate)|gin(?:Event)?)|oun(?:dary|ce)|l(?:ocked|ur)|roadcast|usy)|a(?:n(?:imation(?:iteration|start|end)|tennastatechange)|fter(?:(?:scriptexecu|upda)te|print)|udio(?:process|start|end)|d(?:apteradded|dtrack)|ctivate|lerting|bort)|DOM(?:Node(?:Inserted(?:IntoDocument)?|Removed(?:FromDocument)?)|(?:CharacterData|Subtree)Modified|A(?:ttrModified|ctivate)|Focus(?:Out|In)|MouseScroll)|r(?:e(?:s(?:u(?:m(?:ing|e)|lt)|ize|et)|adystatechange|pea(?:tEven)?t|movetrack|trieving|ceived)|ow(?:s(?:inserted|delete)|e(?:nter|xit))|atechange)|p(?:op(?:up(?:hid(?:den|ing)|show(?:ing|n))|state)|a(?:ge(?:hide|show)|(?:st|us)e|int)|ro(?:pertychange|gress)|lay(?:ing)?)|t(?:ouch(?:(?:lea|mo)ve|en(?:ter|d)|cancel|start)|ime(?:update|out)|ransitionend|ext)|u(?:s(?:erproximity|sdreceived)|p(?:gradeneeded|dateready)|n(?:derflow|load))|f(?:o(?:rm(?:change|input)|cus(?:out|in)?)|i(?:lterchange|nish)|ailed)|l(?:o(?:ad(?:e(?:d(?:meta)?data|nd)|start)?|secapture)|evelchange|y)|g(?:amepad(?:(?:dis)?connected|button(?:down|up)|axismove)|et)|e(?:n(?:d(?:Event|ed)?|abled|ter)|rror(?:update)?|mptied|xit)|i(?:cc(?:cardlockerror|infochange)|n(?:coming|valid|put))|o(?:(?:(?:ff|n)lin|bsolet)e|verflow(?:changed)?|pen)|SVG(?:(?:Unl|L)oad|Resize|Scroll|Abort|Error|Zoom)|h(?:e(?:adphoneschange|l[dp])|ashchange|olding)|v(?:o(?:lum|ic)e|ersion)change|w(?:a(?:it|rn)ing|heel)|key(?:press|down|up)|(?:AppComman|Loa)d|no(?:update|match)|Request|zoom))[\s\0]*=|(\b)(on([a-z])+)(\s)*(=)|javascript|(<\s*)(\/*)script/gi
    );
    let result = regex.test(stringValue);

    if (appConfig.disableXssValidation) {
      result = false;
    }
    return result;
  }

  function isYouTubeEmbeddedLinkDetected(stringValue) {
    const regexYoutube = new RegExp(
      /((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?/
    );
    const result = regexYoutube.test(stringValue);

    return result;
  }

  function isInternalFileDownloadUrlDetected(stringValue) {
    const regexInternalFileDownload = new RegExp(
      /data-url='Files\/Download\//gm
    );
    const result = regexInternalFileDownload.test(stringValue);
    return result;
  }

  function sanitizeString(stringValue) {
    if (
      stringValue &&
      isXssVulnerableString(stringValue) &&
      !isYouTubeEmbeddedLinkDetected(stringValue) &&
      !isInternalFileDownloadUrlDetected(stringValue)
    ) {
      return sanitizeStringExcludingStyle(stringValue);
    } else {
      return stringValue;
    }
  }
  //$sanitize will remove style attributes. to avoid that temporarily replacing styles with a rarely used valid html attribute: abbr
  //and calling $sanitize. then replace the abbr to style and return it as trusted html
  function sanitizeStringExcludingStyle(stringValue) {
    const sanitizedString = $sanitize(
      stringValue.toString().replace(/style=/g, 'abbr=')
    );
    return $sce.trustAsHtml(sanitizedString.replace(/abbr=/g, 'style='));
  }

  function sanitizeHtml(stringValue: string): string {
    if (
      !stringValue ||
      isYouTubeEmbeddedLinkDetected(stringValue) ||
      isInternalFileDownloadUrlDetected(stringValue) ||
      appConfig.disableXssValidation
    ) {
      return stringValue;
    }

    return $sanitize(
      stringValue.toString().replace(/style=/g, 'abbr=')
    ).replace(/abbr=/g, 'style=');
  }

  function getEmailValidationPattern() {
    //return /^[a-zA-Z0-9\-_]+([\.+][a-zA-Z0-9\-_]+)*@[a-z0-9]+(\-[a-z0-9]+)*(\.[a-z0-9]+(\-[a-z0-9]+)*)*$/i;
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  }

  function getNumberValidationPattern() {
    return /^-?(0|[1-9]\d*)$/;
  }

  function getPhoneNumberValidationPattern() {
    return /^\+?\d+$/;
  }

  function isLinkDisallowedForGateway(
    fromnode,
    tonode,
    nodeDataArray,
    linkDataArray
  ) {
    if (!flowinglyGatewayService.isGateway(fromnode)) {
      return false;
    }

    let disallowed = false;

    if (
      flowinglyGatewayService.isDivergeGateway(fromnode.data) &&
      flowinglyGatewayService.isConvergeGateway(tonode.data)
    ) {
      // not allow diverge to merge
      disallowed = true;
    } else if (
      appConfig.workflowParserVersion === 1 &&
      flowinglyGatewayService.isExclusiveGateway(fromnode.data) &&
      flowinglyGatewayService.isConvergeGateway(tonode.data)
    ) {
      // FLOW-3602: not allow exclusive gateway to merge
      disallowed = true;
    } else if (
      flowinglyGatewayService.isConvergeGateway(fromnode.data) &&
      appConfig.workflowParserVersion === 1 &&
      (flowinglyGatewayService.isDivergeGateway(tonode.data) ||
        flowinglyGatewayService.isConvergeGateway(tonode.data))
    ) {
      // not allow merge to diverge or merge
      disallowed = true;
    } else if (
      flowinglyGatewayService.isExclusiveGateway(fromnode.data) &&
      flowinglyActivityService.isActivity(tonode.data) &&
      linkDataArray.some(
        (l) => l.from === fromnode.data.key && l.to === tonode.data.key
      )
    ) {
      // not allow to path same activity again from exclusive gateway
      disallowed = true;
    }

    if (disallowed) {
      console.log('link not allowed for gateway');
    } else {
      if (
        flowinglyGatewayService.isExclusiveGateway(fromnode.data) &&
        getAllowBacklinkFromExclusiveGateway()
      ) {
        disallowed = false;
      } else {
        // refer FLOW-3350
        // back links are not allowed for exclusive gateway,diverge gateway,merge gateway
        const incomingNodes = findIncomingNodes(
          fromnode.data,
          nodeDataArray,
          linkDataArray
        );

        disallowed =
          incomingNodes.filter((n) => n.key === tonode.data.key).length !== 0;
      }
    }

    return disallowed;
  }

  function isLinkDisallowedForStartOrEndNode(fromnode, tonode) {
    if (tonode.data.category === 'event' && tonode.data.text === 'Start')
      return true;

    if (fromnode.data.category === 'event' && fromnode.data.text === 'End') {
      return true;
    }

    return false;
  }

  function isLinkAllowedForNode({
    fromNode,
    toNode,
    nodeDataArray,
    linkDataArray
  }) {
    // FLOW-2585 Only one outbound connector allowed from a Task? Model validation inclusion?
    const linkAlreadyExists = linkDataArray.some(
      (l) => l.from === fromNode.data.key && l.to === toNode.data.key
    );
    if (linkAlreadyExists) {
      return false;
    }

    const fromNodeIsNotActivity = !flowinglyActivityService.isActivity(
      fromNode.data
    );
    const fromNodeIsNotComponent = !flowinglyActivityService.isComponent(
      fromNode.data
    );

    if (fromNodeIsNotActivity && fromNodeIsNotComponent) {
      return true;
    }

    let valid = true;
    const fromNodeFromLinks = linkDataArray.filter(
      (l) => l.from === fromNode.data.key
    );
    const fromNodeFromLinksCount = fromNodeFromLinks.length;
    const isBackLink = isValidBackLink(
      fromNode.data,
      toNode.data,
      nodeDataArray,
      linkDataArray
    );

    if (fromNodeFromLinksCount === 0 && !isBackLink) {
      // check if it's valid forelink
      const allPreviousNodes =
        flowinglyModelUtilityService.getPreviousAcitivityNodesForNodeRecursive(
          fromNode.data.key,
          nodeDataArray,
          linkDataArray,
          null,
          true,
          false
        );
      if (allPreviousNodes.indexOf(toNode.data) > -1) {
        valid = false;
        delete fromNode.data.backlinkTo;
      }
    } else if (fromNodeFromLinksCount === 1) {
      // check if it's one backlink and one forelink
      if (
        (!!fromNodeFromLinks[0].isBacklink && isBackLink) ||
        (!fromNodeFromLinks[0].isBacklink && !isBackLink)
      ) {
        valid = false;
        if (!fromNodeFromLinks[0].isBacklink) {
          delete fromNode.data.backlinkTo;
        }
      }
    } else if (fromNodeFromLinksCount === 2) {
      valid = false;
    }

    return valid;
  }

  function isValidBackLink(
    fromNode: IModelerNode,
    toNode: IModelerNode,
    nodeDataArray: IModelerNode[],
    linkDataArray: IModelerNodeLink[]
  ) {
    if (
      findIncomingNodes(fromNode, nodeDataArray, linkDataArray).some(
        (n) => n.key === toNode.key
      ) &&
      flowinglyGatewayService.isExclusiveGateway(fromNode) &&
      getAllowBacklinkFromExclusiveGateway()
    ) {
      return true;
    }

    // if a link path tonode -> ... -> fromnode which doesn't contain Diverge/Merge gateways exists, then
    // the back link fromnode -> tonode is valid
    if (
      !flowinglyActivityService.isApprovalActivity(fromNode) ||
      !flowinglyActivityService.isActivity(toNode) ||
      toNode.taskType === ActivityTaskType.CUSTOM_EMAIL ||
      toNode.taskType === ActivityTaskType.PUBLIC_FORM
    ) {
      return false;
    }

    const previousNodes =
      flowinglyModelUtilityService.getPreviousAcitivityNodesForNodeRecursive(
        fromNode.key,
        nodeDataArray,
        linkDataArray,
        null,
        true,
        true
      );
    if (previousNodes.indexOf(toNode) > -1) {
      return true;
    }

    return false;
  }

  /* This is a matrix of the expected results
   *
   *      publicFormType  | PublicFormType    | result
   *            0         |        1          | 0
   *            1         |        0          | 1
   *           "1"        |        0          | 1
   *           "0"        |        1          | 0
   *        <null|undef>  |       "1"         | 1
   *        <null|undef>  |        1          | 1
   *        <null|undef>  |       "0"         | 0
   *        <null|undef>  |   <null|undef>    | 0
   */
  function getPublicFormType(someNodeWhoseTypeImUnsureOf) {
    const tmp = someNodeWhoseTypeImUnsureOf;
    const var1 = tmp.publicFormType;
    const var2 = tmp.PublicFormType;

    const v = +coalesce(var1, var2);

    return isNaN(v) ? 0 : v;
  }

  function coalesce(...args) {
    for (const arg of args) {
      if (arg !== null && arg !== undefined && !isNaN(arg)) {
        return arg;
      }
    }
  }

  function findIncomingNodes(
    node,
    nodeDataArray,
    linkDataArray,
    traveledNodes?
  ) {
    const nodes = [];
    traveledNodes = traveledNodes || [];
    const links = _.filter(linkDataArray, function (link) {
      return link.to === node.key && !link.isBacklink;
    });

    if (links) {
      _.forEach(links, function (link) {
        const node = _.find(nodeDataArray, function (node) {
          return node.key === link.from && !link.isBacklink;
        });

        if (
          traveledNodes.find((key) => key === node.key) === undefined &&
          node
        ) {
          traveledNodes.push(node.key);
          const childNodes = findIncomingNodes(
            node,
            nodeDataArray,
            linkDataArray,
            traveledNodes
          );
          _.forEach(childNodes, (n) => {
            nodes.push(n);
          });

          nodes.push(node);
        }
      });
      return nodes;
    }

    return nodes;
  }
}

export type ValidationServiceType = ReturnType<typeof validationService>;
